import React, { useState } from 'react';
import { Layout, Menu, Button, Avatar, Typography, Drawer, Divider } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Settings,
  User,
  LogOut,
  Menu as MenuIcon,
  CloudSun,
  Leaf,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/authService';
import LanguageSelector from '../../components/LanguageSelector';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';

const { Header, Content } = Layout;
const { Text } = Typography;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const SimpleDashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshToken, clearAuth } = useAuthStore();
  const { isDark, mode, setMode } = useTheme();

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    try {
      setLoading(true);
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
      clearAuth();
      navigate('/whatsapp-login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      clearAuth();
      navigate('/whatsapp-login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const { t } = useLanguage();

  // Theme cycle: system → light → dark → system
  const cycleTheme = () => {
    if (mode === 'system') setMode('light');
    else if (mode === 'light') setMode('dark');
    else setMode('system');
  };

  const themeIcon =
    mode === 'dark' ? (
      <Moon className="w-4 h-4" />
    ) : mode === 'light' ? (
      <Sun className="w-4 h-4" />
    ) : (
      <Monitor className="w-4 h-4" />
    );

  const themeTip = mode === 'dark' ? 'Dark' : mode === 'light' ? 'Light' : 'System';

  const menuItems = [
    { key: '/dashboard', icon: <Home className="w-5 h-5" />, label: t('home') },
    { key: '/dashboard/weather-station', icon: <CloudSun className="w-5 h-5" />, label: t('weather_station') },
    { key: '/dashboard/dhartinetra', icon: <Leaf className="w-5 h-5" />, label: t('dhartinetra') },
    { key: '/dashboard/settings', icon: <Settings className="w-5 h-5" />, label: t('settings') },
  ];

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="p-4 flex items-center justify-center">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img
            src="/logo.png"
            alt="QodeAris Logo"
            className="h-8 w-auto"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'https://via.placeholder.com/32x32?text=QA';
            }}
          />
          <span className="text-lg font-bold text-gray-800 dark:text-white">QodeAris</span>
        </Link>
      </div>
      <Menu
        theme={isDark ? 'dark' : 'light'}
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems.map((item) => ({
          key: item.key,
          icon: item.icon,
          label: <Link to={item.key}>{item.label}</Link>,
        }))}
        className="border-r-0 flex-1"
      />
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 mt-auto">
        <Button
          type="primary"
          danger
          icon={<LogOut className="w-4 h-4" />}
          onClick={handleLogout}
          loading={loading}
          className="w-full flex items-center justify-center gap-2"
        >
          {t('logout')}
        </Button>
      </div>
    </div>
  );

  return (
    <Layout className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <Header
        style={{
          padding: '0 16px',
          background: 'var(--color-surface)',
          borderBottom: '1px solid var(--color-border)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div className="flex items-center">
          <Button
            type="text"
            icon={<MenuIcon className="w-5 h-5" />}
            onClick={() => setMobileDrawerOpen(true)}
            aria-label="Menu"
          />
          <span className="ml-3 font-medium text-lg" style={{ color: 'var(--color-text)' }}>
            QodeAris
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Theme cycle button */}
          <Button
            type="text"
            icon={themeIcon}
            onClick={cycleTheme}
            title={`Theme: ${themeTip} — click to cycle`}
            aria-label="Toggle theme"
          />
          <LanguageSelector />
          <Divider type="vertical" className="h-6 mx-1" />
          <div className="flex items-center gap-2">
            <Avatar size="small" icon={<User className="w-4 h-4" />} className="bg-green-500" />
            <Text
              className="hidden sm:inline-block max-w-[100px] truncate"
              style={{ color: 'var(--color-text)' }}
            >
              {user?.name || 'Farmer'}
            </Text>
          </div>
        </div>
      </Header>

      <Content
        style={{
          margin: '16px',
          padding: 16,
          background: 'var(--color-surface)',
          borderRadius: 8,
          minHeight: 280,
          border: '1px solid var(--color-border)',
        }}
      >
        {children}
      </Content>

      <Drawer
        title={t('menu')}
        placement="left"
        onClose={() => setMobileDrawerOpen(false)}
        open={mobileDrawerOpen}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
      >
        {sidebarContent}
      </Drawer>
    </Layout>
  );
};

export default SimpleDashboardLayout;
